// Libs
import classNames from 'classnames/bind';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
// Components, Layouts, Pages
import { ActivityDetailTable } from '~/components/module/activity';
// Others
import { IActivity } from '~/utils/interface/activity';
import { ActivityTypeEnum, TimeFormatEnum } from '~/utils/enum/common';
import { formatDate, formatTime, getFullName } from '~/utils/helper/common';
// Styles, images, icons
import styles from './ActivityDetail.module.scss';

type Props = {
  isOpen: boolean;
  activity?: IActivity;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ActivityDetail = (props: Props) => {
  //#region Destructuring Props
  const { isOpen, activity, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/activity', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [scroll] = useState<DialogProps['scroll']>('paper');
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const renderTitle = () => {
    switch (activity?.actionType) {
      case ActivityTypeEnum.ASSIGNED_JOB:
        return t('activity_title_assigned_job', { ns: 'common/base' });

      case ActivityTypeEnum.ASSIGNED_SCHEDULE:
        return t('activity_title_assigned_schedule', { ns: 'common/base' });

      case ActivityTypeEnum.CHANGE_STATUS_CONTACT:
        return t('activity_title_change_status_contact', { ns: 'common/base' });

      case ActivityTypeEnum.CHANGE_STATUS_CUSTOMER:
        return t('activity_title_change_status_customer', { ns: 'common/base' });

      case ActivityTypeEnum.CREATE_CUSTOMER:
        return t('activity_title_create_customer', { ns: 'common/base' });

      case ActivityTypeEnum.CREATE_FOLDER:
        return t('activity_title_create_folder', { ns: 'common/base' });

      case ActivityTypeEnum.DELETE_FILE:
        return t('activity_title_delete_file', { ns: 'common/base' });

      case ActivityTypeEnum.DELETE_FOLDER:
        return t('activity_title_delete_folder', { ns: 'common/base' });

      case ActivityTypeEnum.UPDATE_CUSTOMER:
        return t('activity_title_update_customer', { ns: 'common/base' });

      case ActivityTypeEnum.UPLOAD_FILE:
        return t('activity_title_upload_file', { ns: 'common/base' });

      case ActivityTypeEnum.CREATE_JOB:
        return t('activity_title_create_job', { ns: 'common/base' });

      case ActivityTypeEnum.CREATE_NOTE:
        return t('activity_title_create_note', { ns: 'common/base' });

      case ActivityTypeEnum.UPDATE_NOTE:
        return t('activity_title_update_note', { ns: 'common/base' });

      case ActivityTypeEnum.DELETE_NOTE:
        return t('activity_title_delete_note', { ns: 'common/base' });

      case ActivityTypeEnum.CREATE_ESTIMATE:
        return t('activity_title_create_estimate', { ns: 'common/base' });

      case ActivityTypeEnum.CHANGE_STATUS_ESTIMATE:
        return t('activity_title_change_status_estimate', { ns: 'common/base' });

      case ActivityTypeEnum.CONVERT_ESTIMATE_TO_JOB:
        return t('activity_title_convert_to_job_estimate', { ns: 'common/base' });

      case ActivityTypeEnum.UPDATE_CUSTOMER_ESTIMATE:
      case ActivityTypeEnum.UPDATE_SCHEDULE_ESTIMATE:
      case ActivityTypeEnum.UPDATE_LINE_ITEM_ESTIMATE:
      case ActivityTypeEnum.UPDATE_PRIVATE_NOTES_ESTIMATE:
      case ActivityTypeEnum.UPDATE_ESTIMATE_FIELDS:
      case ActivityTypeEnum.UPDATE_ESTIMATE_TAGS:
      case ActivityTypeEnum.UPDATE_LEAD_SOURCE_ESTIMATE:
        return t('activity_title_update_estimate', { ns: 'common/base' });

      case ActivityTypeEnum.UPDATE_CUSTOMER_JOB:
      case ActivityTypeEnum.UPDATE_SCHEDULE_JOB:
      case ActivityTypeEnum.UPDATE_LINE_ITEM_JOB:
      case ActivityTypeEnum.UPDATE_PRIVATE_NOTES_JOB:
      case ActivityTypeEnum.UPDATE_JOB_FIELDS:
      case ActivityTypeEnum.UPDATE_JOB_TAGS:
      case ActivityTypeEnum.UPDATE_LEAD_SOURCE_JOB:
      case ActivityTypeEnum.UPDATE_TASKS:
        return t('activity_title_update_job', { ns: 'common/base' });

      default:
        break;
    }
  };

  const handleCloseActivityDetail = () => {
    onClose && onClose();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='activityDetailComponent'
      open={isOpen}
      onClose={handleCloseActivityDetail}
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle>{t('activity_detail_title')}</DialogTitle>

      <DialogContent dividers={scroll === 'paper'}>
        <Typography component={'p'} className={cx('activityValue')}>
          {renderTitle()}
        </Typography>

        <Typography component={'p'} className={cx('activityValue')}>
          {t('activity_detail_update_by', { name: getFullName({ ...activity?.createdBy }) })}
        </Typography>

        <Typography component={'p'} className={cx('activityValue')}>
          {t('activity_detail_update_at', {
            date: `${formatDate(activity?.createdAt)} ${formatTime(
              activity?.createdAt,
              TimeFormatEnum.HH_MM_A_LOWER
            )}`,
          })}
        </Typography>

        {activity &&
          activity.actionType !== ActivityTypeEnum.CREATE_CUSTOMER &&
          activity.actionType !== ActivityTypeEnum.CONVERT_ESTIMATE_TO_JOB && (
            <ActivityDetailTable data={activity} />
          )}
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={handleCloseActivityDetail}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityDetail;
